import { useEffect, useState } from 'react';

type UseDynamicFontSizeOptions = {
  text: string;
  containerWidth: number;
  maxLines?: number;
  lineHeight?: number;
  maxHeight?: number;
  verticalPadding?: number;
  minFontSize?: number;
  maxFontSize?: number;
  baseFontSize?: number;
  fontWeight?: string;
  isMobile?: boolean;
};

export const useDynamicFontSize = ({
  text,
  containerWidth,
  maxLines = 2,
  lineHeight = 1.2,
  maxHeight,
  verticalPadding = 0,
  minFontSize,
  maxFontSize,
  baseFontSize,
  fontWeight = 'normal',
  isMobile,
}: UseDynamicFontSizeOptions): number => {
  const [isMobileState, setIsMobileState] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < 1024 : false
  );

  useEffect(() => {
    const checkMobile = () => {
      setIsMobileState(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const actualIsMobile = isMobile !== undefined ? isMobile : isMobileState;
  const [fontSize, setFontSize] = useState<number>(baseFontSize || (actualIsMobile ? 7 : 14));

  useEffect(() => {
    const calculateFontSize = () => {
      if (!text || text === '-') {
        setFontSize(baseFontSize || (actualIsMobile ? 7 : 14));
        return;
      }

      const availableWidth = containerWidth;
      let minSize = minFontSize || 6;
      let maxSize = maxFontSize || (actualIsMobile ? 8 : 18);
      const baseSize = baseFontSize || (actualIsMobile ? 7 : 14);
      let optimalSize = baseSize;

      // Calculate max text height if maxHeight is provided
      const maxTextHeight = maxHeight ? maxHeight - verticalPadding : undefined;

      // Create a temporary element for measurement
      const measure = document.createElement('div');
      measure.style.position = 'absolute';
      measure.style.visibility = 'hidden';
      measure.style.whiteSpace = 'pre-wrap';
      measure.style.wordWrap = 'break-word';
      measure.style.fontWeight = fontWeight;
      measure.style.width = `${availableWidth}px`;
      document.body.appendChild(measure);

      // Binary search for optimal font size
      while (minSize <= maxSize) {
        const testSize = Math.floor((minSize + maxSize) / 2);

        measure.style.fontSize = `${testSize}px`;
        measure.style.lineHeight = `${lineHeight}`;
        measure.textContent = text;

        // Force reflow
        void measure.offsetHeight;

        const textHeight = measure.scrollHeight;
        const calculatedMaxHeight = testSize * lineHeight * maxLines;
        const actualMaxHeight = maxTextHeight ? Math.min(calculatedMaxHeight, maxTextHeight) : calculatedMaxHeight;

        if (textHeight <= actualMaxHeight) {
          optimalSize = testSize;
          minSize = testSize + 1;
        } else {
          maxSize = testSize - 1;
        }
      }

      document.body.removeChild(measure);
      setFontSize(optimalSize);
    };

    if (containerWidth > 0) {
      const timeoutId = setTimeout(calculateFontSize, 0);
      window.addEventListener('resize', calculateFontSize);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', calculateFontSize);
      };
    }
  }, [
    text,
    containerWidth,
    maxLines,
    lineHeight,
    maxHeight,
    verticalPadding,
    minFontSize,
    maxFontSize,
    baseFontSize,
    fontWeight,
    actualIsMobile,
  ]);

  return fontSize;
};
